import type { ShareAccess } from "../domain/share";
import type { RemoteRoomVoteInput } from "../domain/remote-room-record";
import type { SyncrelState } from "../domain/types";

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

export interface RemoteAccessOptions {
  access?: ShareAccess;
  share?: string | null;
  fetcher?: Fetcher;
}

export const loadRemoteRoomSnapshot = async (
  room: string,
  options: RemoteAccessOptions = {},
): Promise<SyncrelState | null> => {
  const share = options.share?.trim();
  if (!share) return null;
  const access = options.access ?? "view";
  const fetcher = fetcherFromOptions(options);

  try {
    const response = await fetcher(remoteRoomUrl(room, access, share), {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (response.ok && isJsonResponse(response)) {
      const payload = await response.json() as { snapshot?: SyncrelState };
      return payload.snapshot ?? null;
    }
    if (!shouldTryPhpFallback(response)) return null;
  } catch {
    // Static shared hosting cannot serve /api routes; try the colocated PHP endpoint below.
  }

  return loadPhpRoomSnapshot(room, access, share, fetcher);
};

export const saveRemoteRoomSnapshot = async (
  state: SyncrelState,
  options: RemoteAccessOptions = {},
): Promise<void> => {
  const share = options.share?.trim() || state.workspace.share?.editKey;
  if (!share) return;
  const fetcher = fetcherFromOptions(options);

  try {
    const response = await fetcher(`/api/rooms/${encodeURIComponent(state.workspace.room)}`, {
      method: "PUT",
      headers: jsonHeaders(),
      body: JSON.stringify({
        access: "edit",
        share,
        state,
      }),
    });
    if (writeSucceeded(response)) return;
    if (!shouldTryPhpFallback(response)) {
      await drainResponse(response);
      return;
    }
  } catch {
    // Remote persistence is a durable sharing enhancement; local/Yjs state stays authoritative in-browser.
  }

  await savePhpRoomSnapshot(state, share, fetcher);
};

export const saveRemotePollVote = async (
  room: string,
  vote: RemoteRoomVoteInput,
  options: RemoteAccessOptions = {},
): Promise<void> => {
  const share = options.share?.trim();
  if (!share) return;
  const access = options.access ?? "view";
  const fetcher = fetcherFromOptions(options);

  try {
    const response = await fetcher(`/api/rooms/${encodeURIComponent(room)}/votes`, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({
        access,
        share,
        vote,
      }),
    });
    if (writeSucceeded(response)) return;
    if (!shouldTryPhpFallback(response)) {
      await drainResponse(response);
      return;
    }
  } catch {
    // Poll votes still apply locally; the API write is best-effort when serverless storage is unavailable.
  }

  await savePhpPollVote(room, vote, access, share, fetcher);
};

export const remoteAccessFromUrl = (href?: string): RemoteAccessOptions => {
  const url = new URL(href ?? currentHref());
  const requested = url.searchParams.get("access");
  return {
    access: requested === "edit" ? "edit" : "view",
    share: url.searchParams.get("share"),
  };
};

const remoteRoomUrl = (room: string, access: ShareAccess, share: string): string => (
  `/api/rooms/${encodeURIComponent(room)}?${new URLSearchParams({ access, share }).toString()}`
);

const phpRoomUrl = (room: string, params: Record<string, string>): string => (
  `/save.php?${new URLSearchParams({ room, ...params }).toString()}`
);

const jsonHeaders = (): HeadersInit => ({
  Accept: "application/json",
  "Content-Type": "application/json",
});

const fetcherFromOptions = (options: RemoteAccessOptions): Fetcher => (
  options.fetcher ?? fetch
);

const currentHref = (): string => {
  if (typeof window !== "undefined" && window.location?.href) return window.location.href;
  return "http://localhost/";
};

const loadPhpRoomSnapshot = async (
  room: string,
  access: ShareAccess,
  share: string,
  fetcher: Fetcher,
): Promise<SyncrelState | null> => {
  try {
    const response = await fetcher(phpRoomUrl(room, { access, share }), {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!response.ok || !isJsonResponse(response)) return null;
    const payload = await response.json() as { snapshot?: SyncrelState };
    return payload.snapshot ?? null;
  } catch {
    return null;
  }
};

const savePhpRoomSnapshot = async (
  state: SyncrelState,
  share: string,
  fetcher: Fetcher,
): Promise<void> => {
  try {
    const response = await fetcher(phpRoomUrl(state.workspace.room, { mode: "snapshot" }), {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({
        access: "edit",
        share,
        state,
      }),
    });
    await drainResponse(response);
  } catch {
    // Local/Yjs state remains usable when shared-hosting persistence is unavailable.
  }
};

const savePhpPollVote = async (
  room: string,
  vote: RemoteRoomVoteInput,
  access: ShareAccess,
  share: string,
  fetcher: Fetcher,
): Promise<void> => {
  try {
    const response = await fetcher(phpRoomUrl(room, { mode: "vote" }), {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({
        access,
        share,
        vote,
      }),
    });
    await drainResponse(response);
  } catch {
    // Poll votes still apply locally even if the PHP write is unavailable.
  }
};

const isJsonResponse = (response: Response): boolean => (
  response.headers.get("Content-Type")?.toLowerCase().includes("application/json") ?? false
);

const writeSucceeded = (response: Response): boolean => (
  response.ok && (response.status === 204 || isJsonResponse(response))
);

const shouldTryPhpFallback = (response: Response): boolean => (
  response.status === 404 ||
  response.status === 405 ||
  response.status >= 500 ||
  (response.ok && response.status !== 204 && !isJsonResponse(response))
);

const drainResponse = async (response: Response): Promise<void> => {
  if (!response.ok) {
    await response.text().catch(() => "");
  }
};
